import { Box, Typography, Divider } from "@mui/joy";
import { useEffect } from "react";
import { RotateCw } from "lucide-react";

import { TaskActionComponent, TaskHeader } from "../../Tasks/components";
import { DocumentPreviewWidgetV2, type IMenuAction } from "../../";
import { useMaterialOrdering } from "../hooks";
import { OrderSummary } from "./OrderSummary";
import { useDocument } from "../../../hooks/query";
import { useProject } from "../../../hooks";
import { useNotification } from "../../../hooks/useNotification";

interface ITaskComponent {
    activeTask: any;
    onComplete: any;
    actionProgress: any;
}

function isDocumentGenerated(doc: any) {
    return !!doc?.DocumentAssetId && doc?.Status === "GENERATED";
}

export default function TaskComponent(props: ITaskComponent) {
    const { activeTask, onComplete, actionProgress } = props;
    const { Id } = activeTask || {};
    const { documents, refreshDocuments } = useMaterialOrdering();
    const { project } = useProject();
    const { useSupplyOrderCreate, useWorkOrderCreate, useGutterOrderCreate } =
        useDocument();
    const { showSuccess, showError } = useNotification();
    const createSupplyOrderSvc = useSupplyOrderCreate();
    const createWorkOrderSvc = useWorkOrderCreate();
    const createGutterOrderSvc = useGutterOrderCreate();

    const supplyOrderDoc = documents?.["SUPPLY_ORDER"];
    const gutterOrderDoc = documents?.["GUTTER_ORDER"];
    const workOrderDoc = documents?.["WORK_ORDER"];

    const handleGenerateSupplyOrder = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await createSupplyOrderSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Supply order generated successfully");
            setTimeout(() => {
                refreshDocuments();
            }, 1000);
        } catch (error) {
            console.error("Error generating supply order:", error);
            showError("Failed to generate supply order");
        }
    };

    const handleGenerateWorkOrder = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await createWorkOrderSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Work order generated successfully");
            setTimeout(() => {
                refreshDocuments();
            }, 1000);
        } catch (error) {
            console.error("Error generating work order:", error);
            showError("Failed to generate work order");
        }
    };

    const handleGenerateGutterOrder = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await createGutterOrderSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Gutter order generated successfully");
            setTimeout(() => {
                refreshDocuments();
            }, 1000);
        } catch (error) {
            console.error("Error generating gutter order:", error);
            showError("Failed to generate gutter order");
        }
    };

    useEffect(() => {
        const scrollableDiv = document.getElementById("project-details-parent");
        if (scrollableDiv) {
            scrollableDiv.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, []);

    const supplyOrderMenuActions: IMenuAction[] = isDocumentGenerated(supplyOrderDoc)
        ? [
              {
                  label: "Regenerate",
                  icon: <RotateCw size={16} />,
                  onClick: handleGenerateSupplyOrder,
                  disabled: createSupplyOrderSvc.isPending,
              },
          ]
        : [];

    const gutterOrderMenuActions: IMenuAction[] = isDocumentGenerated(gutterOrderDoc)
        ? [
              {
                  label: "Regenerate",
                  icon: <RotateCw size={16} />,
                  onClick: handleGenerateGutterOrder,
                  disabled: createGutterOrderSvc.isPending,
              },
          ]
        : [];

    const workOrderMenuActions: IMenuAction[] = isDocumentGenerated(workOrderDoc)
        ? [
              {
                  label: "Regenerate",
                  icon: <RotateCw size={16} />,
                  onClick: handleGenerateWorkOrder,
                  disabled: createWorkOrderSvc.isPending,
              },
          ]
        : [];

    return (
        <Box sx={{ position: "relative", minHeight: 400 }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    mt: 1,
                    px: { xs: 2, sm: 3 },
                    animation: "fadeIn 0.5s ease-in-out",
                    "@keyframes fadeIn": {
                        "0%": { opacity: 0 },
                        "100%": { opacity: 1 },
                    },
                }}
            >
                <TaskHeader activeTask={activeTask} />
                <Divider />

                <Box mt={2.5}>
                    <Typography
                        level="body-sm"
                        sx={{ color: "text.primary", fontWeight: 600 }}
                        gutterBottom
                    >
                        Order Summary
                    </Typography>
                    <Divider sx={{ mt: 0.5, mb: 1 }} />
                    <OrderSummary />
                </Box>

                <Box mt={3}>
                    <Typography
                        level={"body-sm"}
                        sx={{ color: "text.primary", fontWeight: 600 }}
                        gutterBottom
                    >
                        Document Review
                    </Typography>
                    <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                        Material quantities are automatically calculated based on the roof
                        area and other project factors. To maintain accurate estimations,
                        manual adjustments are not recommended.
                    </Typography>
                    <Box
                        mt={2}
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(auto-fill, minmax(280px, 1fr))",
                            },
                            gap: 2.5,
                        }}
                    >
                        <DocumentPreviewWidgetV2
                            title="Supply Order.pdf"
                            description={
                                supplyOrderDoc
                                    ? `List of materials required · ${supplyOrderDoc.Status}`
                                    : "Click to generate supply order"
                            }
                            fileType="PDF"
                            assetId={supplyOrderDoc?.DocumentAssetId}
                            onButtonClick={
                                isDocumentGenerated(supplyOrderDoc)
                                    ? undefined
                                    : handleGenerateSupplyOrder
                            }
                            loading={createSupplyOrderSvc.isPending}
                            menuActions={supplyOrderMenuActions}
                        />
                        <DocumentPreviewWidgetV2
                            title="Gutter Order.pdf"
                            description={
                                gutterOrderDoc
                                    ? `Gutter material specifications · ${gutterOrderDoc.Status}`
                                    : "Click to generate gutter order"
                            }
                            fileType="PDF"
                            assetId={gutterOrderDoc?.DocumentAssetId}
                            onButtonClick={
                                isDocumentGenerated(gutterOrderDoc)
                                    ? undefined
                                    : handleGenerateGutterOrder
                            }
                            loading={createGutterOrderSvc.isPending}
                            menuActions={gutterOrderMenuActions}
                        />
                        <DocumentPreviewWidgetV2
                            title="Work Order.pdf"
                            description={
                                workOrderDoc
                                    ? `Materials for assigned work · ${workOrderDoc.Status}`
                                    : "Click to generate work order"
                            }
                            fileType="PDF"
                            assetId={workOrderDoc?.DocumentAssetId}
                            onButtonClick={
                                isDocumentGenerated(workOrderDoc)
                                    ? undefined
                                    : handleGenerateWorkOrder
                            }
                            loading={createWorkOrderSvc.isPending}
                            menuActions={workOrderMenuActions}
                        />
                    </Box>
                </Box>

                <Box mt={4}>
                    <Divider />
                    <TaskActionComponent
                        taskName={activeTask?.Name || ""}
                        onComplete={onComplete}
                        actionProgress={actionProgress}
                        isNextEligible={true}
                        taskId={Id || ""}
                    />
                </Box>
            </Box>
        </Box>
    );
}
